import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getIsOnline } from "@/lib/connectivity";

export interface PDVOperator {
  id: string;
  userId: string;
  name: string;
  role: string;
  initials: string;
  pin: string;
}

const roleLabels: Record<string, string> = {
  restaurant_owner: "Proprietário",
  manager: "Gerente",
  cashier: "Caixa",
  waiter: "Garçom",
  kitchen: "Cozinha",
  super_admin: "Admin",
};

function getInitials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

const CACHE_KEY_PREFIX = "pdv:operators:";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

function loadCachedOperators(restaurantId: string): PDVOperator[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY_PREFIX + restaurantId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { savedAt: number; operators: PDVOperator[] };
    if (!parsed?.operators?.length) return null;
    const expired = Date.now() - parsed.savedAt > CACHE_TTL_MS;
    if (expired) {
      console.warn("[usePDVOperators] Cache expired but using as fallback");
    }
    return parsed.operators;
  } catch {
    return null;
  }
}

function saveCachedOperators(restaurantId: string, operators: PDVOperator[]) {
  try {
    localStorage.setItem(
      CACHE_KEY_PREFIX + restaurantId,
      JSON.stringify({ savedAt: Date.now(), operators })
    );
  } catch (err) {
    console.warn("[usePDVOperators] Failed to cache operators:", err);
  }
}

export function usePDVOperators(restaurantId: string | undefined) {
  const [operators, setOperators] = useState<PDVOperator[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchOperators = useCallback(async () => {
    if (!restaurantId) {
      setOperators([]);
      setIsLoading(false);
      return;
    }

    // If offline, hydrate immediately from cache and skip network
    if (!getIsOnline()) {
      const cached = loadCachedOperators(restaurantId);
      if (cached) {
        console.log(`[usePDVOperators] Offline - hydrated ${cached.length} operators from cache`);
        setOperators(cached);
      } else {
        console.warn("[usePDVOperators] Offline and no cache available");
      }
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("restaurant_users")
        .select("id, user_id, role, operator_pin, profile:profiles(full_name)")
        .eq("restaurant_id", restaurantId)
        .not("operator_pin", "is", null);

      if (error) throw error;

      const ops: PDVOperator[] = (data || [])
        .filter((d: any) => d.operator_pin)
        .map((d: any) => {
          const name = d.profile?.full_name || "Sem nome";
          return {
            id: d.id,
            userId: d.user_id,
            name,
            role: roleLabels[d.role] || d.role,
            initials: getInitials(name),
            pin: d.operator_pin,
          };
        });

      setOperators(ops);
      saveCachedOperators(restaurantId, ops);
    } catch (err) {
      console.error("Error fetching PDV operators:", err);
      // Fallback to cache on network/Supabase error
      const cached = loadCachedOperators(restaurantId);
      if (cached) {
        console.log(`[usePDVOperators] Fetch failed - using ${cached.length} cached operators`);
        setOperators(cached);
      }
    } finally {
      setIsLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    fetchOperators();
  }, [fetchOperators]);

  const validatePin = useCallback(
    (operatorId: string, pin: string): boolean => {
      const op = operators.find((o) => o.id === operatorId);
      return op?.pin === pin;
    },
    [operators]
  );

  return { operators, isLoading, validatePin, refetch: fetchOperators };
}
